import React, { useState, useEffect } from 'react';
import { PostSessionResult } from '../services/PostSessionPipelineService';
import { FirebaseClinicalNotesService } from '../services/firebase/FirebaseClinicalNotesService';
import PDFGeneratorService from '../services/PDFGeneratorService';
import { useFocusTrap } from '../hooks/useFocusTrap';
import VoiceInputButton from './VoiceInputButton';

type NoteTemplate = 'SOAP' | 'DAP' | 'BIRP' | 'Progress' | 'Initial';

interface PostSessionReviewModalProps {
  result: PostSessionResult;
  appointmentId: string;
  patientId: string;
  patientName: string;
  providerId: string;
  onClose: () => void;
  onNoteSaved?: () => void;
}

// Template section definitions
const TEMPLATE_SECTIONS: Record<NoteTemplate, { key: string; label: string; placeholder: string }[]> = {
  SOAP: [
    { key: 'subjective', label: 'Subjective', placeholder: 'Patient\'s reported symptoms, concerns, and history...' },
    { key: 'objective', label: 'Objective', placeholder: 'Observable data, clinical observations, mental status...' },
    { key: 'assessment', label: 'Assessment', placeholder: 'Clinical assessment, diagnoses, and formulation...' },
    { key: 'plan', label: 'Plan', placeholder: 'Treatment plan, interventions, follow-up...' },
  ],
  DAP: [
    { key: 'data', label: 'Data', placeholder: 'Objective and subjective data from the session...' },
    { key: 'assessment', label: 'Assessment', placeholder: 'Clinical interpretation and analysis...' },
    { key: 'plan', label: 'Plan', placeholder: 'Next steps, goals, and interventions...' },
  ],
  BIRP: [
    { key: 'behavior', label: 'Behavior', placeholder: 'Client\'s presenting behavior and observations...' },
    { key: 'intervention', label: 'Intervention', placeholder: 'Therapeutic interventions used during session...' },
    { key: 'response', label: 'Response', placeholder: 'Client\'s response to interventions...' },
    { key: 'plan', label: 'Plan', placeholder: 'Plan for continued treatment...' },
  ],
  Progress: [
    { key: 'sessionFocus', label: 'Session Focus', placeholder: 'Primary topics and issues addressed...' },
    { key: 'interventions', label: 'Interventions', placeholder: 'Therapeutic techniques and approaches used...' },
    { key: 'progress', label: 'Progress', placeholder: 'Client\'s progress toward goals...' },
    { key: 'nextSteps', label: 'Next Steps', placeholder: 'Plans for upcoming sessions...' },
  ],
  Initial: [
    { key: 'presentingProblem', label: 'Presenting Problem', placeholder: 'Reason for referral and chief complaint...' },
    { key: 'history', label: 'History', placeholder: 'Relevant medical, psychiatric, social history...' },
    { key: 'mentalStatusExam', label: 'Mental Status Exam', placeholder: 'Appearance, mood, affect, thought process...' },
    { key: 'assessment', label: 'Assessment', placeholder: 'Diagnostic impressions and formulation...' },
    { key: 'plan', label: 'Treatment Plan', placeholder: 'Proposed treatment plan and recommendations...' },
  ],
};

const PostSessionReviewModal: React.FC<PostSessionReviewModalProps> = ({
  result,
  appointmentId,
  patientId,
  patientName,
  providerId,
  onClose,
  onNoteSaved,
}) => {
  const [activeView, setActiveView] = useState<'note' | 'transcript' | 'summary'>('note');
  const [selectedTemplate, setSelectedTemplate] = useState<NoteTemplate>(
    (result.noteSuggestion?.template as NoteTemplate) || 'SOAP'
  );
  const [sectionData, setSectionData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [providerDisplayName, setProviderDisplayName] = useState('Provider');

  // Load provider name for transcript labels
  useEffect(() => {
    PDFGeneratorService.fetchPracticeInfo(providerId).then(info => {
      if (info.providerName) setProviderDisplayName(info.providerName);
    }).catch(() => {});
  }, [providerId]);

  // Parse raw AI note content into SOAP sections
  const parseContentIntoSections = (content: string, template: NoteTemplate): Record<string, string> => {
    const sections = TEMPLATE_SECTIONS[template];
    const parsed: Record<string, string> = {};

    // Build regex patterns for section headers (handles "SUBJECTIVE:", "--- SUBJECTIVE:", "## Subjective", etc.)
    const sectionKeys = sections.map(s => s.key);
    const sectionLabels = sections.map(s => s.label.toUpperCase());
    // Also match common SOAP variations
    const headerAliases: Record<string, string[]> = {
      subjective: ['SUBJECTIVE', 'SUBJECTIVE COMPLAINT', 'S:'],
      objective: ['OBJECTIVE', 'OBJECTIVE FINDINGS', 'O:'],
      assessment: ['ASSESSMENT', 'ASSESSMENT OF PROGRESS', 'A:'],
      plan: ['PLAN', 'PLANS FOR NEXT SESSION', 'P:', 'PLAN:'],
      data: ['DATA', 'D:'],
      behavior: ['BEHAVIOR', 'B:'],
      intervention: ['INTERVENTION', 'I:'],
      response: ['RESPONSE', 'R:'],
      progress: ['PROGRESS', 'PROGRESS SUMMARY'],
      goals: ['GOALS', 'TREATMENT GOALS', 'TREATMENT GOALS ADDRESSED'],
      barriers: ['BARRIERS', 'BARRIERS TO PROGRESS'],
      nextSteps: ['NEXT STEPS'],
      presentingProblem: ['PRESENTING PROBLEM'],
      history: ['RELEVANT HISTORY', 'HISTORY'],
      mentalStatus: ['MENTAL STATUS', 'MENTAL STATUS EXAM', 'MSE'],
      diagnosis: ['DIAGNOSIS', 'DIAGNOSTIC IMPRESSION'],
      recommendations: ['RECOMMENDATIONS'],
    };

    // Try to split content by section headers
    let remaining = content;
    const found: { key: string; start: number; }[] = [];

    for (const section of sections) {
      const aliases = headerAliases[section.key] || [section.label.toUpperCase()];
      for (const alias of aliases) {
        // Match patterns like "--- SUBJECTIVE:", "## Subjective", "SUBJECTIVE:", "**Subjective**"
        const pattern = new RegExp(`(?:^|\\n)\\s*(?:---\\s*)?(?:##\\s*)?(?:\\*\\*)?${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\*\\*)?\\s*:?\\s*(?:---)?\\s*`, 'i');
        const match = remaining.match(pattern);
        if (match && match.index !== undefined) {
          found.push({ key: section.key, start: match.index });
          break;
        }
      }
    }

    if (found.length >= 2) {
      // Sort by position and extract text between headers
      found.sort((a, b) => a.start - b.start);
      for (let i = 0; i < found.length; i++) {
        const headerEnd = content.indexOf('\n', found[i].start + 1);
        const startPos = headerEnd !== -1 ? headerEnd + 1 : found[i].start;
        const endPos = i + 1 < found.length ? found[i + 1].start : content.length;
        const sectionText = content.slice(startPos, endPos).trim();
        parsed[found[i].key] = sectionText;
      }
      return parsed;
    }

    // Fallback: put everything in first section
    if (sections.length > 0) {
      parsed[sections[0].key] = content;
    }
    return parsed;
  };

  // Initialize section data from AI suggestion
  useEffect(() => {
    if (result.noteSuggestion?.sections && Object.keys(result.noteSuggestion.sections).length > 0) {
      setSectionData({ ...result.noteSuggestion.sections });
    } else if (result.noteSuggestion?.content) {
      // Parse raw content into structured sections
      setSectionData(parseContentIntoSections(result.noteSuggestion.content, selectedTemplate));
    }
  }, [result.noteSuggestion]);

  const handleSectionChange = (key: string, value: string) => {
    setSectionData(prev => ({ ...prev, [key]: value }));
  };

  const buildNoteContent = (): string => {
    const sections = TEMPLATE_SECTIONS[selectedTemplate];
    return sections
      .map(s => `## ${s.label}\n${sectionData[s.key] || ''}`)
      .join('\n\n');
  };

  const handleSaveNote = async (status: 'draft' | 'signed') => {
    setSaving(true);
    setSaveError(null);
    try {
      const content = buildNoteContent();
      const aiContent = result.noteSuggestion?.content || '';

      await FirebaseClinicalNotesService.createNote({
        sessionId: appointmentId,
        patientId,
        providerId,
        template: selectedTemplate,
        content,
        aiGeneratedContent: aiContent,
        wasAiAssisted: true,
        providerEdited: content !== aiContent,
        version: 1,
        editHistory: [{
          timestamp: new Date().toISOString(),
          editorId: providerId,
          changeType: 'ai-generated' as any,
          description: 'AI-generated from session transcript',
        }],
        status,
        ...(status === 'signed' ? { signedBy: providerId, signedAt: new Date() } : {}),
      });

      setSaved(true);
      onNoteSaved?.();
    } catch (error: any) {
      console.error('Error saving clinical note:', error);
      setSaveError(error.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = (template: NoteTemplate) => {
    setSelectedTemplate(template);
    // If AI sections exist and match, try to remap
    if (result.noteSuggestion?.sections) {
      setSectionData({ ...result.noteSuggestion.sections });
    }
  };

  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportTranscriptPDF = async () => {
    if (!result.transcription) return;
    setExportingPDF(true);
    try {
      const pdfService = PDFGeneratorService.getInstance();
      const practiceInfo = await PDFGeneratorService.fetchPracticeInfo(providerId);
      const blob = pdfService.generateTranscriptPDF({
        patientName,
        providerName: practiceInfo.providerName || 'Provider',
        sessionDate: new Date(),
        duration: result.transcription.duration || 0,
        speakerCount: result.transcription.speakerCount || 2,
        utterances: result.transcription.utterances,
        diarizedText: result.transcription.diarizedText,
        highlights: result.transcription.highlights,
        practiceInfo,
      });
      const filename = `Transcript_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdfService.downloadPDF(blob, filename);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportingPDF(false);
    }
  };

  const { transcription, noteSuggestion, summary } = result;
  const durationMinutes = transcription?.duration ? Math.round(transcription.duration / 60) : 0;

  // Focus trap for accessibility
  const modalRef = useFocusTrap<HTMLDivElement>(true, onClose);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Post-session review for ${patientName}`}
        style={{
        backgroundColor: '#1a2332',
        borderRadius: '12px',
        width: '90vw',
        maxWidth: '1200px',
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #6CA6C1',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#16325B',
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#F7E9A0', fontSize: '1.25rem', fontWeight: '700' }}>
              Post-Session Review
            </h2>
            <p style={{ margin: '4px 0 0', color: '#e5e7eb', fontSize: '0.875rem' }}>
              Session with {patientName}
              {durationMinutes > 0 && ` \u2022 ${durationMinutes} minutes`}
              {transcription?.speakerCount && ` \u2022 ${transcription.speakerCount} speakers detected`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Pipeline status badges */}
            {transcription && (
              <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(16,185,129,0.2)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.3)' }}>
                Transcribed
              </span>
            )}
            {noteSuggestion && (
              <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(212,160,23,0.2)', color: '#F7E9A0', border: '1px solid rgba(212,160,23,0.3)' }}>
                AI Note Ready
              </span>
            )}
            {summary && (
              <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: 'rgba(59,130,246,0.2)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.3)' }}>
                Summary Ready
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px',
                marginLeft: '8px',
              }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '0',
          borderBottom: '1px solid #2d3748',
          backgroundColor: '#1e2d3d',
        }}>
          {[
            { id: 'note' as const, label: 'Clinical Note', icon: '\uD83D\uDCDD', disabled: !noteSuggestion },
            { id: 'transcript' as const, label: 'Transcript', icon: '\uD83C\uDFA4', disabled: !transcription },
            { id: 'summary' as const, label: 'Session Summary', icon: '\uD83D\uDCCB', disabled: !summary },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveView(tab.id)}
              disabled={tab.disabled}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderBottom: activeView === tab.id ? '3px solid #D4A017' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: tab.disabled ? '#4b5563' : activeView === tab.id ? '#F7E9A0' : '#e5e7eb',
                fontSize: '0.875rem',
                fontWeight: activeView === tab.id ? '600' : '400',
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {/* ============ NOTE VIEW ============ */}
          {activeView === 'note' && (
            <div>
              {noteSuggestion ? (
                <>
                  {/* Template Selector + Confidence */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label style={{ color: '#e5e7eb', fontSize: '0.875rem', fontWeight: '600' }}>Template:</label>
                      {(['SOAP', 'DAP', 'BIRP', 'Progress', 'Initial'] as NoteTemplate[]).map(t => (
                        <button
                          key={t}
                          onClick={() => handleTemplateChange(t)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            border: selectedTemplate === t ? '1px solid #D4A017' : '1px solid #4b5563',
                            backgroundColor: selectedTemplate === t ? 'rgba(212,160,23,0.15)' : 'transparent',
                            color: selectedTemplate === t ? '#F7E9A0' : '#d1d5db',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {noteSuggestion.confidence && (
                      <span style={{
                        color: noteSuggestion.confidence > 0.8 ? '#6EE7B7' : noteSuggestion.confidence > 0.6 ? '#FDE68A' : '#FCA5A5',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                      }}>
                        AI Confidence: {Math.round(noteSuggestion.confidence * 100)}%
                      </span>
                    )}
                  </div>

                  {/* Warnings */}
                  {noteSuggestion.warnings && noteSuggestion.warnings.length > 0 && (
                    <div style={{
                      backgroundColor: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                    }}>
                      <p style={{ color: '#FCA5A5', fontSize: '0.8rem', fontWeight: '600', margin: '0 0 4px' }}>
                        AI Warnings:
                      </p>
                      {noteSuggestion.warnings.map((w, i) => (
                        <p key={i} style={{ color: '#FCA5A5', fontSize: '0.8rem', margin: '2px 0' }}>- {w}</p>
                      ))}
                    </div>
                  )}

                  {/* Section Fields */}
                  {TEMPLATE_SECTIONS[selectedTemplate].map(section => (
                    <div key={section.key} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <label style={{
                          color: '#F7E9A0',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                        }}>
                          {section.label}
                        </label>
                        <VoiceInputButton onResult={text => handleSectionChange(section.key, (sectionData[section.key] || '') + ' ' + text)} />
                      </div>
                      <textarea
                        value={sectionData[section.key] || ''}
                        onChange={(e) => handleSectionChange(section.key, e.target.value)}
                        placeholder={section.placeholder}
                        style={{
                          width: '100%',
                          minHeight: '120px',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #4b5563',
                          backgroundColor: '#0f1a2b',
                          color: '#e5e7eb',
                          fontSize: '0.875rem',
                          lineHeight: '1.6',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>
                  ))}

                  {/* Save Actions */}
                  {!saved ? (
                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                      <button
                        onClick={() => handleSaveNote('draft')}
                        disabled={saving}
                        style={{
                          padding: '10px 24px',
                          borderRadius: '8px',
                          border: '1px solid #6CA6C1',
                          backgroundColor: '#28527A',
                          color: '#e5e7eb',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          opacity: saving ? 0.6 : 1,
                        }}
                      >
                        {saving ? 'Saving...' : 'Save as Draft'}
                      </button>
                      <button
                        onClick={() => handleSaveNote('signed')}
                        disabled={saving}
                        style={{
                          padding: '10px 24px',
                          borderRadius: '8px',
                          border: '1px solid #D4A017',
                          backgroundColor: 'rgba(212,160,23,0.2)',
                          color: '#F7E9A0',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          opacity: saving ? 0.6 : 1,
                        }}
                      >
                        {saving ? 'Saving...' : 'Sign & Finalize'}
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(16,185,129,0.1)',
                      border: '1px solid rgba(16,185,129,0.3)',
                      color: '#6EE7B7',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      marginTop: '20px',
                    }}>
                      Clinical note saved successfully. You can review it in the client's Notes tab.
                    </div>
                  )}
                  {saveError && (
                    <p style={{ color: '#FCA5A5', fontSize: '0.8rem', marginTop: '8px' }}>{saveError}</p>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                  <p style={{ fontSize: '1.25rem', marginBottom: '8px' }}>No AI note was generated</p>
                  <p style={{ fontSize: '0.875rem' }}>
                    The transcription may have failed or the session was too short. You can create a note manually from the client's Notes tab.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ============ TRANSCRIPT VIEW ============ */}
          {activeView === 'transcript' && (
            <div>
              {transcription ? (
                <>
                  {/* Transcript Stats */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                  }}>
                    {[
                      { label: 'Duration', value: `${durationMinutes} min` },
                      { label: 'Speakers', value: `${transcription.speakerCount}` },
                      { label: 'Utterances', value: `${transcription.utterances?.length || 0}` },
                    ].map(stat => (
                      <div key={stat.label} style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        backgroundColor: '#0f1a2b',
                        border: '1px solid #2d3748',
                      }}>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>{stat.label}</p>
                        <p style={{ color: '#e5e7eb', fontSize: '1.125rem', fontWeight: '600', margin: '2px 0 0' }}>{stat.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Export PDF Button */}
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      onClick={handleExportTranscriptPDF}
                      disabled={exportingPDF}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '6px',
                        border: '1px solid #D4A017',
                        backgroundColor: 'rgba(212,160,23,0.15)',
                        color: '#F7E9A0',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: exportingPDF ? 'not-allowed' : 'pointer',
                        opacity: exportingPDF ? 0.6 : 1,
                      }}
                    >
                      {exportingPDF ? 'Generating PDF...' : 'Export Transcript as PDF'}
                    </button>
                  </div>

                  {/* Highlights */}
                  {transcription.highlights && transcription.highlights.length > 0 && (
                    <div style={{
                      marginBottom: '20px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(212,160,23,0.08)',
                      border: '1px solid rgba(212,160,23,0.2)',
                    }}>
                      <p style={{ color: '#F7E9A0', fontSize: '0.8rem', fontWeight: '600', margin: '0 0 6px' }}>
                        Key Highlights
                      </p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {transcription.highlights.slice(0, 10).map((h, i) => (
                          <span key={i} style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            backgroundColor: 'rgba(212,160,23,0.15)',
                            color: '#F7E9A0',
                            fontSize: '0.75rem',
                          }}>
                            {h.text}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Diarized Transcript */}
                  <div style={{
                    backgroundColor: '#0f1a2b',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    padding: '20px',
                  }}>
                    <h3 style={{ color: '#e5e7eb', fontSize: '0.9rem', fontWeight: '600', margin: '0 0 16px' }}>
                      Session Transcript
                    </h3>
                    {transcription.utterances && transcription.utterances.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {transcription.utterances.map((utterance) => (
                          <div key={utterance.id} style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'flex-start',
                          }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: utterance.speaker === 'A' ? 'rgba(16,185,129,0.2)' : 'rgba(59,130,246,0.2)',
                              color: utterance.speaker === 'A' ? '#6EE7B7' : '#93C5FD',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                              marginTop: '2px',
                            }}>
                              {utterance.speaker === 'A' ? providerDisplayName : patientName}
                            </span>
                            <p style={{
                              margin: 0,
                              color: '#e5e7eb',
                              fontSize: '0.875rem',
                              lineHeight: '1.6',
                            }}>
                              {utterance.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre style={{
                        color: '#e5e7eb',
                        fontSize: '0.875rem',
                        lineHeight: '1.8',
                        whiteSpace: 'pre-wrap',
                        margin: 0,
                        fontFamily: 'inherit',
                      }}>
                        {transcription.diarizedText || transcription.text}
                      </pre>
                    )}
                  </div>

                  {/* Sentiment Analysis */}
                  {transcription.sentiment && transcription.sentiment.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h3 style={{ color: '#e5e7eb', fontSize: '0.9rem', fontWeight: '600', margin: '0 0 12px' }}>
                        Sentiment Analysis
                      </h3>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {transcription.sentiment.slice(0, 8).map((s, i) => (
                          <div key={i} style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            backgroundColor: '#0f1a2b',
                            border: `1px solid ${
                              s.sentiment === 'POSITIVE' ? 'rgba(16,185,129,0.3)' :
                              s.sentiment === 'NEGATIVE' ? 'rgba(239,68,68,0.3)' : 'rgba(107,114,128,0.3)'
                            }`,
                          }}>
                            <span style={{
                              fontSize: '0.75rem',
                              color: s.sentiment === 'POSITIVE' ? '#6EE7B7' :
                                s.sentiment === 'NEGATIVE' ? '#FCA5A5' : '#9ca3af',
                              fontWeight: '500',
                            }}>
                              {s.sentiment} ({Math.round(s.confidence * 100)}%)
                            </span>
                            <p style={{ margin: '4px 0 0', color: '#d1d5db', fontSize: '0.8rem' }}>
                              "{s.text.substring(0, 60)}{s.text.length > 60 ? '...' : ''}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                  <p style={{ fontSize: '1.25rem', marginBottom: '8px' }}>No transcript available</p>
                  <p style={{ fontSize: '0.875rem' }}>
                    Audio recording or transcription failed for this session.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ============ SUMMARY VIEW ============ */}
          {activeView === 'summary' && (
            <div>
              {summary ? (
                <>
                  {/* Risk Flags */}
                  {summary.riskFlags && summary.riskFlags.length > 0 && (
                    <div style={{
                      backgroundColor: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '20px',
                    }}>
                      <h3 style={{ color: '#FCA5A5', fontSize: '0.9rem', fontWeight: '700', margin: '0 0 8px' }}>
                        Risk Flags
                      </h3>
                      {summary.riskFlags.map((flag, i) => (
                        <p key={i} style={{ color: '#FCA5A5', fontSize: '0.875rem', margin: '4px 0' }}>
                          &bull; {flag}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Two-Column Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                    {/* Provider Summary */}
                    <div style={{
                      backgroundColor: '#0f1a2b',
                      borderRadius: '8px',
                      border: '1px solid #2d3748',
                      padding: '16px',
                    }}>
                      <h3 style={{ color: '#F7E9A0', fontSize: '0.9rem', fontWeight: '600', margin: '0 0 12px' }}>
                        Provider Summary
                      </h3>
                      <p style={{ color: '#e5e7eb', fontSize: '0.875rem', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                        {summary.providerSummary}
                      </p>
                    </div>

                    {/* Client Summary */}
                    <div style={{
                      backgroundColor: '#0f1a2b',
                      borderRadius: '8px',
                      border: '1px solid #2d3748',
                      padding: '16px',
                    }}>
                      <h3 style={{ color: '#93C5FD', fontSize: '0.9rem', fontWeight: '600', margin: '0 0 12px' }}>
                        Client Summary (Patient-Facing)
                      </h3>
                      <p style={{ color: '#e5e7eb', fontSize: '0.875rem', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                        {summary.clientSummary}
                      </p>
                    </div>
                  </div>

                  {/* Detail Sections */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {/* Key Topics */}
                    <SummaryList
                      title="Key Topics"
                      items={summary.keyTopics}
                      color="#F7E9A0"
                    />

                    {/* Interventions Used */}
                    <SummaryList
                      title="Interventions Used"
                      items={summary.interventionsUsed}
                      color="#6EE7B7"
                    />

                    {/* Homework Assigned */}
                    <SummaryList
                      title="Homework / Between-Session Tasks"
                      items={summary.homeworkAssigned}
                      color="#93C5FD"
                    />

                    {/* Next Session Focus */}
                    <SummaryList
                      title="Next Session Focus"
                      items={summary.nextSessionFocus}
                      color="#C4B5FD"
                    />
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                  <p style={{ fontSize: '1.25rem', marginBottom: '8px' }}>No summary available</p>
                  <p style={{ fontSize: '0.875rem' }}>
                    Summary generation failed or transcription was not available.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #2d3748',
          backgroundColor: '#16325B',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>
            AI-generated content is a suggestion only. Always review and edit before finalizing.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              border: '1px solid #4b5563',
              backgroundColor: 'transparent',
              color: '#e5e7eb',
              fontSize: '0.8rem',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper component for summary lists
const SummaryList: React.FC<{ title: string; items: string[]; color: string }> = ({ title, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      backgroundColor: '#0f1a2b',
      borderRadius: '8px',
      border: '1px solid #2d3748',
      padding: '16px',
    }}>
      <h4 style={{ color, fontSize: '0.8rem', fontWeight: '600', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {title}
      </h4>
      {items.map((item, i) => (
        <p key={i} style={{ color: '#e5e7eb', fontSize: '0.85rem', margin: '4px 0' }}>
          &bull; {item}
        </p>
      ))}
    </div>
  );
};

export default PostSessionReviewModal;
